import React, { useCallback, useEffect, useState } from 'react';
import { PageHeader, Panel, Skeleton, Icon, useToast } from '../../components/ui';
import { getOutreachSettings, saveOutreachSettings, lookupOutreachArea } from '../../utils/api';
import ConfirmDialog from '../../marketing/components/ConfirmDialog';
import { fmtDateTime, loadError } from '../utils';

const EFFORTS = ['low', 'medium', 'high'];
const MAX_AREAS = 40;

// Area keys are stable identifiers stored on every office, so they are derived
// from the label once (while the row is new) and never change after saving.
const slug = (s) => String(s || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 40);
const splitList = (text, sep) => String(text || '').split(sep).map(s => s.trim()).filter(Boolean);
const CITY_SEP = /[,;\n]+/;
const ZIP_SEP = /[\s,;]+/;
const CENSUS_SEP = /[;\n]+/;
const ZIP5 = /^\d{5}$/;

// The form keeps each list as free text while the admin types; it is split
// into arrays only on save so a comma mid-edit never scatters the row.
function areaRow(a) {
  return { ...a, citiesText: (a.nppesCities || []).join(', '), zipsText: (a.zips || []).join(' '), censusText: (a.censusNames || []).join('; ') };
}
function areaBody(a) {
  return {
    areaKey: a.areaKey, label: a.label, primary: Boolean(a.primary), enabled: a.enabled !== false,
    nppesCities: splitList(a.citiesText, CITY_SEP), zips: splitList(a.zipsText, ZIP_SEP), censusNames: splitList(a.censusText, CENSUS_SEP),
  };
}
function areaProblems(areas) {
  if (!areas.length) return 'Keep at least one service area.';
  if (areas.length > MAX_AREAS) return `At most ${MAX_AREAS} service areas are allowed.`;
  const keys = new Set();
  for (const a of areas) {
    if (!String(a.label || '').trim()) return 'Every area needs a name.';
    if (!a.areaKey) return `Area "${a.label}" needs a name that contains a letter or number.`;
    if (keys.has(a.areaKey)) return `Two areas share the key "${a.areaKey}". Give one of them a different name.`;
    keys.add(a.areaKey);
    const zips = splitList(a.zipsText, ZIP_SEP);
    if (!splitList(a.citiesText, CITY_SEP).length && !zips.length) return `Area "${a.label}" needs at least one city or ZIP code so offices can be matched to it.`;
    const badZip = zips.find(z => !/^\d{5}$/.test(z));
    if (badZip) return `"${badZip}" in ${a.label} is not a five digit ZIP code.`;
  }
  if (!areas.some(a => a.enabled !== false)) return 'At least one area must be enabled.';
  return '';
}

function Field({ id, label, hint, children }) {
  return (
    <div className="ops-field">
      <label htmlFor={id}>{label}{hint && <span className="ops-field__hint"> {hint}</span>}</label>
      {children}
    </div>
  );
}

// One public data source: a switch with a plain-language blurb, and its
// options shown only while it is on so the list stays short.
function SourceRow({ id, title, blurb, enabled, onToggle, children }) {
  return (
    <div className={`ops-src${enabled ? ' is-on' : ''}`}>
      <label className="ops-src__head" htmlFor={id}>
        <input id={id} type="checkbox" checked={enabled} onChange={e => onToggle(e.target.checked)} />
        <span className="ops-src__text"><strong>{title}</strong><span>{blurb}</span></span>
      </label>
      {enabled && children && <div className="ops-src__body">{children}</div>}
    </div>
  );
}

// Every change is sent with the record version. A 409 means someone else saved first;
// the form reloads with their values so nothing is overwritten blind.
export default function SettingsPage() {
  const [showToast, toastNode] = useToast();
  const [data, setData] = useState(null);
  const [form, setForm] = useState(null);
  const [recipientsText, setRecipientsText] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [pendingRemove, setPendingRemove] = useState(null);
  const [newZip, setNewZip] = useState('');
  // 'new' while the add box is looking up, or the index of the card being refilled.
  const [lookingUp, setLookingUp] = useState(null);

  const load = useCallback(() => getOutreachSettings().then(r => {
    setData(r);
    const settings = JSON.parse(JSON.stringify(r.settings));
    settings.areas = (settings.areas || []).map(areaRow);
    setForm(settings);
    setRecipientsText((r.settings.notifications?.recipients || []).join('\n'));
    setError('');
  }).catch(e => setError(loadError(e, 'Could not load outreach settings'))), []);
  useEffect(() => { load(); }, [load]);

  const set = (path, value) => setForm(f => {
    const next = JSON.parse(JSON.stringify(f));
    const keys = path.split('.');
    let cur = next;
    for (let i = 0; i < keys.length - 1; i += 1) cur = cur[keys[i]];
    cur[keys[keys.length - 1]] = value;
    return next;
  });

  const addArea = () => setForm(f => ({ ...f, areas: [...f.areas, areaRow({ areaKey: '', label: '', primary: false, enabled: true, isNew: true })] }));

  // The lookup returns spellings, ZIPs and Census names read from NPPES and the
  // Census geocoder. They land on the card as a draft the admin checks and saves;
  // areaBody drops the draft notes so they never reach the API.
  const draftFrom = (area, existingKeys) => {
    const warnings = [...(area.warnings || [])];
    const key = slug(area.label);
    if (existingKeys.has(key)) warnings.push(`An area with the key "${key}" already exists. Give this one a different name or remove the duplicate.`);
    return {
      citiesText: (area.nppesCities || []).join(', '), zipsText: (area.zips || []).join(' '), censusText: (area.censusNames || []).join('; '),
      lookupNote: `Filled from NPPES and the Census geocoder for ${area.zip}${area.county ? ` in ${area.county}` : ''}, based on ${area.practices} practice locations. Check the spellings, then save.`,
      lookupWarnings: warnings, lookupError: '',
    };
  };
  const addAreaFromZip = async () => {
    const zip = newZip.trim();
    if (!ZIP5.test(zip) || lookingUp !== null) return;
    setLookingUp('new');
    let row;
    try {
      const { area } = await lookupOutreachArea(zip);
      row = { ...areaRow({ areaKey: slug(area.label), label: area.label, primary: false, enabled: true, isNew: true }), ...draftFrom(area, new Set(form.areas.map(a => a.areaKey))) };
    } catch (err) {
      row = { ...areaRow({ areaKey: '', label: '', primary: false, enabled: true, isNew: true, zips: [zip] }), lookupError: `${err.message || `Could not look up ${zip}.`} Fill the fields in by hand or try again.` };
    }
    setForm(f => ({ ...f, areas: [...f.areas, row] }));
    setNewZip('');
    setLookingUp(null);
  };
  const fillFromZip = async (i) => {
    const zip = splitList(form.areas[i].zipsText, ZIP_SEP)[0];
    if (!zip || !ZIP5.test(zip)) { set(`areas.${i}.lookupError`, 'Enter a five digit ZIP code in the ZIP codes field first.'); return; }
    setLookingUp(i);
    try {
      const { area } = await lookupOutreachArea(zip);
      setForm(f => {
        const next = JSON.parse(JSON.stringify(f));
        const row = next.areas[i];
        if (!row) return f;
        const others = new Set(next.areas.filter((_, idx) => idx !== i).map(a => a.areaKey));
        Object.assign(row, draftFrom(area, String(row.label || '').trim() ? new Set() : others));
        if (!String(row.label || '').trim()) { row.label = area.label; if (row.isNew) row.areaKey = slug(area.label); }
        return next;
      });
    } catch (err) {
      set(`areas.${i}.lookupError`, err.message || `Could not look up ${zip}.`);
    } finally { setLookingUp(null); }
  };
  const setAreaLabel = (i, label) => setForm(f => {
    const next = JSON.parse(JSON.stringify(f));
    next.areas[i].label = label;
    if (next.areas[i].isNew) next.areas[i].areaKey = slug(label);
    return next;
  });
  // Only one area is primary: it leads the reports and the area list.
  const setPrimary = (i) => setForm(f => ({ ...f, areas: f.areas.map((a, idx) => ({ ...a, primary: idx === i })) }));
  const removeArea = (i) => { setForm(f => ({ ...f, areas: f.areas.filter((_, idx) => idx !== i) })); setPendingRemove(null); };
  const requestRemove = (i) => (form.areas[i].isNew ? removeArea(i) : setPendingRemove(i));

  const submit = async (e) => {
    e.preventDefault();
    const problem = areaProblems(form.areas);
    if (problem) { setSaveError(problem); return; }
    setSaving(true); setSaveError('');
    try {
      const body = {
        version: form.version,
        ai: form.ai,
        schedule: form.schedule,
        sources: form.sources,
        notifications: { ...form.notifications, recipients: recipientsText.split(/[\n,;]+/).map(s => s.trim()).filter(Boolean) },
        areas: form.areas.map(areaBody),
        compliance: form.compliance,
      };
      const r = await saveOutreachSettings(body);
      showToast(r.scheduleSync?.error ? 'Settings saved, but the schedule could not be updated' : 'Settings saved', r.scheduleSync?.error ? 'error' : 'success');
      await load();
    } catch (err) {
      if (err.status === 409) { setSaveError(err.message); await load(); }
      else setSaveError(err.message || 'Could not save settings');
    } finally { setSaving(false); }
  };

  if (error) return <><PageHeader title="Outreach settings" /><div className="ops-error">{error}</div></>;
  if (!form) return <><PageHeader title="Outreach settings" /><Panel><Skeleton rows={8} /></Panel></>;

  const live = data.schedule || {};
  const drift = live.configured && !live.error && (Boolean(live.enabled) !== Boolean(data.settings.schedule?.enabled) || (live.expression && live.expression !== data.settings.schedule?.expression));
  const ai = form.ai;
  // The list comes from the API; a saved model outside it still shows so the
  // field never looks blank.
  const modelOptions = [...(data.modelOptions || [])];
  if (ai.modelId && !modelOptions.some(m => m.id === ai.modelId)) modelOptions.push({ id: ai.modelId, label: ai.modelId });
  const src = form.sources;
  const mdhhsDatasets = data.mdhhsDatasets || [];
  const mdhhsChoice = src.mdhhs.dataset || 'custom';
  const mdhhsPicked = mdhhsDatasets.find(d => d.id === mdhhsChoice);
  const enabledAreas = form.areas.filter(a => a.enabled !== false).length;
  const atMax = form.areas.length >= MAX_AREAS;
  const zipReady = ZIP5.test(newZip.trim()) && lookingUp === null && !atMax;

  return (
    <form onSubmit={submit}>
      <PageHeader
        title="Outreach settings"
        subtitle="Controls for the research agent: where it looks, which public sources it uses, how it spends AI budget and whether it runs on a schedule."
        meta={data.settings.updatedAt ? `Last saved ${fmtDateTime(data.settings.updatedAt)}${data.settings.updatedBy ? ` by ${data.settings.updatedBy}` : ''}` : 'Defaults in use'}
        actions={<button type="submit" className="ops-btn ops-btn--primary ops-btn--sm" disabled={saving}><Icon name="check" size={14} /> {saving ? 'Saving' : 'Save settings'}</button>}
      />
      {saveError && <div className="ops-error" style={{ marginBottom: 14 }}>{saveError}</div>}

      <div className="ops-grid-2">
        <Panel title="Weekly schedule">
          <label className="ops-check" style={{ marginBottom: 10 }}>
            <input type="checkbox" checked={Boolean(form.schedule.enabled)} onChange={e => set('schedule.enabled', e.target.checked)} /> Run incremental research automatically
          </label>
          <div className="ops-form-grid">
            <Field id="s-expr" label="Schedule" hint="cron(...) or rate(...)"><input id="s-expr" className="ops-input mono" value={form.schedule.expression} onChange={e => set('schedule.expression', e.target.value)} /></Field>
            <Field id="s-tz" label="Timezone"><input id="s-tz" className="ops-input" value={form.schedule.timezone} onChange={e => set('schedule.timezone', e.target.value)} /></Field>
          </div>
          <div className="ops-help" style={{ marginTop: 8 }}>Default is Monday 7:00 AM Detroit time. Scheduled runs never email anyone and never bypass the AI cache.</div>
          <div className={`ops-note${drift || live.error ? ' ops-note--review' : ''}`} style={{ marginTop: 10 }}>
            <strong>Live AWS schedule.</strong>{' '}
            {!live.configured ? 'Not deployed in this environment.' : live.error ? `Could not read it: ${live.error}` : `${live.enabled ? 'Enabled' : 'Disabled'}, ${live.expression} (${live.timezone}).`}
            {drift && <div className="ops-help" style={{ marginTop: 4 }}>This differs from the saved settings. Saving again re-syncs it. A stack deploy also resets the schedule to disabled.</div>}
          </div>
        </Panel>

        <Panel title="Report emails">
          <label className="ops-check" style={{ marginBottom: 10 }}>
            <input type="checkbox" checked={Boolean(form.notifications.emailOnManualRun)} onChange={e => set('notifications.emailOnManualRun', e.target.checked)} /> Pre-tick the email option when staff start a run
          </label>
          <Field id="n-rcpt" label="Recipients" hint="one address per line, up to 10">
            <textarea id="n-rcpt" className="ops-input" rows={4} value={recipientsText} onChange={e => setRecipientsText(e.target.value)} />
          </Field>
          <div className="ops-help" style={{ marginTop: 8 }}>Reports list office-level changes only and are sent from the agency's notify.homecareofmi.com address.</div>
        </Panel>
      </div>

      <Panel title="AI model" className="ops-panel" >
        <div className="ops-form-grid">
          <Field id="ai-model" label="Model" hint="Gemini 2.5 Flash is the default; a model Gemini rejects fails the run and says so"><select id="ai-model" className="ops-select" value={ai.modelId} onChange={e => set('ai.modelId', e.target.value)}>{modelOptions.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}</select></Field>
          <Field id="ai-effort" label="Reasoning effort" hint="low turns thinking off; medium and high give the model a thinking budget"><select id="ai-effort" className="ops-select" value={ai.reasoningEffort} onChange={e => set('ai.reasoningEffort', e.target.value)}>{EFFORTS.map(x => <option key={x} value={x}>{x}</option>)}</select></Field>
          <Field id="ai-calls" label="Max AI calls per run"><input id="ai-calls" type="number" min={0} max={2000} className="ops-input" value={ai.maxAiCallsPerRun} onChange={e => set('ai.maxAiCallsPerRun', e.target.value)} /></Field>
          <Field id="ai-tokens" label="Max output tokens per call"><input id="ai-tokens" type="number" min={200} max={8000} className="ops-input" value={ai.maxOutputTokens} onChange={e => set('ai.maxOutputTokens', e.target.value)} /></Field>
          <Field id="ai-cap" label="Cost cap per run (USD)" hint="blank disables the cap"><input id="ai-cap" type="number" min={0} step="0.01" className="ops-input" value={ai.maxCostPerRunUsd ?? ''} onChange={e => set('ai.maxCostPerRunUsd', e.target.value)} /></Field>
          <Field id="ai-in" label="Input price per 1M tokens (USD)" hint="from the Gemini price list"><input id="ai-in" type="number" min={0} step="0.01" className="ops-input" value={ai.inputCostPerMillionUsd ?? ''} onChange={e => set('ai.inputCostPerMillionUsd', e.target.value)} /></Field>
          <Field id="ai-out" label="Output price per 1M tokens (USD)"><input id="ai-out" type="number" min={0} step="0.01" className="ops-input" value={ai.outputCostPerMillionUsd ?? ''} onChange={e => set('ai.outputCostPerMillionUsd', e.target.value)} /></Field>
        </div>
        <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
          <label className="ops-check"><input type="checkbox" checked={Boolean(ai.areaIntelligence)} onChange={e => set('ai.areaIntelligence', e.target.checked)} /> Produce area intelligence summaries</label>
          <label className="ops-check"><input type="checkbox" checked={Boolean(ai.changeSummary)} onChange={e => set('ai.changeSummary', e.target.checked)} /> Let the model write the run summary when there are changes</label>
          <label className="ops-check"><input type="checkbox" checked={Boolean(ai.includeStaffNotes)} onChange={e => set('ai.includeStaffNotes', e.target.checked)} /> Include staff notes in office analysis (off by default)</label>
        </div>
        <div className={`ops-note${data.geminiKey?.configured === false ? ' ops-note--review' : ''}`} style={{ marginTop: 12 }}>
          <strong>Gemini API key.</strong>{' '}
          {data.geminiKey?.configured === true && `Configured${data.geminiKey.lastChangedAt ? `, last changed ${fmtDateTime(data.geminiKey.lastChangedAt)}` : ''}.`}
          {data.geminiKey?.configured === false && 'Not found. Research runs that need AI analysis will fail until the key exists.'}
          {data.geminiKey?.configured === null && `Could not check the key: ${data.geminiKey.error}`}
          {' '}This is the same key the notes, prior authorization and payments features use. It lives only in AWS Secrets Manager as <span className="mono">{data.secretName}</span> and is managed from the AWS console; it is never sent to the browser. The model is never changed silently: a missing or unavailable model fails the run and says so.
          <div className="ops-help" style={{ marginTop: 6 }}>Cost estimates stay empty until both token prices are entered.</div>
        </div>
      </Panel>

      <Panel
        title="Service areas"
        count={`${enabledAreas} of ${form.areas.length} researched`}
        className="ops-panel"
        action={(
          <div className="ops-area-add">
            <input className="ops-input mono" inputMode="numeric" maxLength={5} placeholder="ZIP code" aria-label="ZIP code of the area to add" value={newZip} disabled={lookingUp !== null || atMax}
              onChange={e => setNewZip(e.target.value.replace(/\D/g, '').slice(0, 5))}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addAreaFromZip(); } }} />
            <button type="button" className="ops-btn ops-btn--primary ops-btn--sm" onClick={addAreaFromZip} disabled={!zipReady}>
              {lookingUp === 'new' ? <><Icon name="clock" size={14} /> Looking up</> : <><Icon name="plus" size={14} /> Add area</>}
            </button>
          </div>
        )}
      >
        <div className="ops-help" style={{ marginBottom: 14 }}>
          Each area is a community the agent researches. Type one ZIP code and click Add area: the city spellings practices use in the NPPES registry, the other ZIP codes for that city and the Census names are filled in from public registries. Nothing is saved until you click Save settings. The primary area leads reports and the area list.
          {' '}<button type="button" className="ops-link" onClick={addArea} disabled={atMax}>Add a blank area instead</button>
        </div>
        <div className="ops-area-grid">
          {form.areas.map((a, i) => {
            const paused = a.enabled === false;
            return (
              <div key={a.isNew ? `new-${i}` : a.areaKey} className={`ops-area${a.primary ? ' is-primary' : ''}${paused ? ' is-paused' : ''}`}>
                <div className="ops-area__head">
                  <div className="ops-area__name">
                    <input className="ops-input" aria-label="Area name" placeholder="Area name, for example Rochester Hills" value={a.label} onChange={e => setAreaLabel(i, e.target.value)} autoFocus={Boolean(a.isNew) && !a.label} />
                    <div className="ops-area__key">{a.areaKey ? `Key ${a.areaKey}${a.isNew ? '' : ', fixed once saved'}` : 'Key appears as you type'}</div>
                  </div>
                  <button type="button" className="ops-btn ops-btn--ghost ops-btn--sm ops-btn--icon" aria-label={`Remove ${a.label || 'area'}`} title="Remove area" onClick={() => requestRemove(i)}><Icon name="trash" size={14} /></button>
                </div>
                <div className="ops-area__status">
                  <label className="ops-check"><input type="checkbox" checked={!paused} onChange={e => set(`areas.${i}.enabled`, e.target.checked)} /> {paused ? 'Research paused' : 'Research on'}</label>
                  {a.primary
                    ? <span className="ops-pill ops-pill--blue">Primary area</span>
                    : <button type="button" className="ops-link" onClick={() => setPrimary(i)}>Make primary</button>}
                </div>
                {a.lookupError && <div className="ops-error ops-area__note">{a.lookupError}</div>}
                {a.lookupNote && (
                  <div className={`ops-note ops-area__note${a.lookupWarnings?.length ? ' ops-note--review' : ''}`}>
                    <strong>Draft.</strong> {a.lookupNote}
                    {a.lookupWarnings?.length > 0 && <ul>{a.lookupWarnings.map(w => <li key={w}>{w}</li>)}</ul>}
                  </div>
                )}
                <div className="ops-area__fields">
                  <Field id={`area-${i}-cities`} label="Cities" hint="as NPPES spells them, commas between">
                    <textarea id={`area-${i}-cities`} className="ops-input" rows={2} placeholder="PONTIAC, PONTIAC TWP" value={a.citiesText} onChange={e => set(`areas.${i}.citiesText`, e.target.value)} />
                  </Field>
                  <Field id={`area-${i}-zips`} label="ZIP codes" hint="five digits, spaces between">
                    <input id={`area-${i}-zips`} className="ops-input mono" placeholder="48340 48341 48342" value={a.zipsText} onChange={e => set(`areas.${i}.zipsText`, e.target.value)} />
                    <button type="button" className="ops-link ops-area__fill" onClick={() => fillFromZip(i)} disabled={lookingUp !== null}>{lookingUp === i ? 'Looking up' : 'Fill cities and Census names from the first ZIP'}</button>
                  </Field>
                  <Field id={`area-${i}-census`} label="Census names" hint="optional, semicolons between">
                    <textarea id={`area-${i}-census`} className="ops-input" rows={2} placeholder="Pontiac city, Michigan" value={a.censusText} onChange={e => set(`areas.${i}.censusText`, e.target.value)} />
                  </Field>
                </div>
              </div>
            );
          })}
        </div>
        <div className="ops-help" style={{ marginTop: 14 }}>
          Census names must match a Census place or township exactly, for example "Pontiac city, Michigan" or "Waterford charter township, Oakland County, Michigan". Fill from the first ZIP looks them up for you. Census figures cover Michigan cities and the townships of Wayne, Oakland and Macomb County. Pausing an area keeps its saved offices but stops new research for it. Removing an area also keeps its saved offices; they stay in the directory under the old area name until a person archives or reassigns them.
        </div>
      </Panel>

      <div className="ops-grid-2" style={{ marginTop: 18 }}>
        <Panel title="Public sources">
          <div className="ops-src-list">
            <SourceRow id="src-nppes" title="CMS NPPES registry" blurb="The national provider registry. This is where most new offices come from." enabled={Boolean(src.nppes.enabled)} onToggle={v => set('sources.nppes.enabled', v)}>
              <Field id="src-pages" label="Pages per city" hint="1 to 6, more pages find more offices but take longer"><input id="src-pages" type="number" min={1} max={6} className="ops-input" value={src.nppes.maxPages} onChange={e => set('sources.nppes.maxPages', e.target.value)} /></Field>
              <label className="ops-check"><input type="checkbox" checked={Boolean(src.nppes.includeIndividuals)} onChange={e => set('sources.nppes.includeIndividuals', e.target.checked)} /> Also include individual providers, not only practices</label>
            </SourceRow>
            <SourceRow id="src-hrsa-on" title="HRSA health center sites" blurb="Federally funded health centers, which serve Medicaid patients by design." enabled={Boolean(src.hrsa.enabled)} onToggle={v => set('sources.hrsa.enabled', v)}>
              <Field id="src-hrsa" label="Dataset URL" hint="hrsa.gov only"><input id="src-hrsa" className="ops-input mono" value={src.hrsa.datasetUrl || ''} onChange={e => set('sources.hrsa.datasetUrl', e.target.value)} /></Field>
            </SourceRow>
            <SourceRow id="src-mdhhs-on" title="Medicaid plan provider directories" blurb="MDHHS publishes no machine-readable list of enrolled providers. Each Michigan Medicaid Health Plan must publish one, and these are the files the agent can read." enabled={Boolean(src.mdhhs.enabled)} onToggle={v => set('sources.mdhhs.enabled', v)}>
              <Field id="src-mdhhs-dataset" label="Dataset" hint={mdhhsPicked ? `About ${mdhhsPicked.sizeMb} MB from ${mdhhsPicked.url}` : 'A CSV published on michigan.gov'}>
                <select id="src-mdhhs-dataset" className="ops-select" value={mdhhsChoice} onChange={e => set('sources.mdhhs.dataset', e.target.value)}>
                  {mdhhsDatasets.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
                  <option value="custom">Custom CSV on michigan.gov</option>
                </select>
              </Field>
              {mdhhsChoice === 'custom' && <Field id="src-mdhhs" label="Dataset URL" hint="michigan.gov only"><input id="src-mdhhs" className="ops-input mono" value={src.mdhhs.datasetUrl || ''} onChange={e => set('sources.mdhhs.datasetUrl', e.target.value)} /></Field>}
            </SourceRow>
            <SourceRow id="src-census-on" title="US Census American Community Survey" blurb="Population and Medicaid coverage figures for each area." enabled={Boolean(src.census.enabled)} onToggle={v => set('sources.census.enabled', v)}>
              <Field id="src-year" label="ACS year" hint="5-year release; a year's figures come out the following December"><input id="src-year" type="number" min={2015} max={new Date().getFullYear() - 1} className="ops-input" value={src.census.year} onChange={e => set('sources.census.year', e.target.value)} /></Field>
              <div className="ops-help">If the year is not published yet the run uses the latest release that is and says so in the run report.</div>
            </SourceRow>
            <SourceRow id="src-web-on" title="Official practice websites" blurb="Reads each practice's own site for hours, phone numbers and accepted insurance." enabled={Boolean(src.website.enabled)} onToggle={v => set('sources.website.enabled', v)}>
              <Field id="src-sites" label="Max sites per run"><input id="src-sites" type="number" min={0} max={500} className="ops-input" value={src.website.maxSites} onChange={e => set('sources.website.maxSites', e.target.value)} /></Field>
              <div className="ops-help">Sites are fetched only from the practice's own domain found in the registries, over https, with no redirects to other hosts.</div>
            </SourceRow>
          </div>
        </Panel>

        <Panel title="Marketing compliance">
          <label className="ops-check"><input type="checkbox" checked={Boolean(form.compliance.requireMarketingReview)} onChange={e => set('compliance.requireMarketingReview', e.target.checked)} /> Require a compliance review before outreach material names a health plan</label>
          <div style={{ marginTop: 10 }}><Field id="c-notes" label="Review notes"><textarea id="c-notes" className="ops-input" rows={3} maxLength={2000} value={form.compliance.reviewNotes || ''} onChange={e => set('compliance.reviewNotes', e.target.value)} /></Field></div>
          <div className="ops-help" style={{ marginTop: 8 }}>This tool researches offices from public data only. It never collects or stores Medicaid member information.</div>
        </Panel>
      </div>

      <div className="ops-actions" style={{ justifyContent: 'flex-end', marginTop: 18 }}>
        <button type="button" className="ops-btn ops-btn--ghost" onClick={load} disabled={saving}>Discard changes</button>
        <button type="submit" className="ops-btn ops-btn--primary" disabled={saving}>{saving ? 'Saving' : 'Save settings'}</button>
      </div>
      {pendingRemove !== null && form.areas[pendingRemove] && (
        <ConfirmDialog title={`Remove ${form.areas[pendingRemove].label || 'this area'}?`} confirmLabel="Yes, remove" onConfirm={() => removeArea(pendingRemove)} onCancel={() => setPendingRemove(null)}>
          The agent stops researching this area on the next run. Offices already saved for it are kept and still appear in the directory. If you only want to pause research, untick Research on instead. Nothing changes until you click Save settings.
        </ConfirmDialog>
      )}
      {toastNode}
    </form>
  );
}
